import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatTile } from "./_shared";
import { PaperPlaneTilt } from "@phosphor-icons/react";

const TARGETS = [
  { v: "all", l: "All users" },
  { v: "buyers", l: "All buyers" },
  { v: "sellers", l: "All sellers" },
  { v: "stale_buyers", l: "Stale buyers (>7d)" },
  { v: "live_offer_sellers", l: "Sellers with live offers active" },
];

const PRESETS = [
  { id: "match", title: "New matches available", body: "We've added 3 cars matching your saved requirement. Tap to view." },
  { id: "stale", title: "Still looking?", body: "Your sourcing request is going stale. Refresh to receive new matches." },
  { id: "inspection", title: "Inspection reminder", body: "Your inspection is scheduled for tomorrow. Our executive will reach out." },
  { id: "offer", title: "Live buyer activity", body: "Multiple verified buyers are competing for your vehicle right now." },
];

export default function Notifications() {
  const [target, setTarget] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/admin/notifications/stats").then(r => setStats(r.data)); }, []);

  const applyPreset = (p) => { setTitle(p.title); setBody(p.body); };

  const send = async () => {
    if (!title || !body) { toast.error("Title and body required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", { title, body, target });
      toast.success(`Sent to ${data.sent} users`);
      setTitle(""); setBody("");
      api.get("/admin/notifications/stats").then(r => setStats(r.data));
    } catch (e) { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-notifications">
      <PageHeader title="Push Notification Controls" subtitle="Smart Broadcasts" />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] mb-6">
          <StatTile label="Total Sent" value={stats.total} accent />
          <StatTile label="Seen" value={stats.seen} />
          <StatTile label="Open Rate" value={`${stats.open_rate}%`} accent />
          <StatTile label="Match Alerts" value={stats.by_kind?.match || 0} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2A2A2A]">
        <div className="bg-[#0E0E0E] p-5 lg:col-span-2">
          <div className="qd-overline mb-4">Composer</div>

          <div className="mb-4">
            <label className="qd-label">Target Audience</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="qd-input-block" data-testid="notif-target">
              {TARGETS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>

          <div className="mb-4">
            <label className="qd-label">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="qd-input-block" placeholder="Eg. New SUVs matching your search" data-testid="notif-title" />
          </div>

          <div className="mb-4">
            <label className="qd-label">Body</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} className="qd-input-block min-h-[100px]" placeholder="Short message…" data-testid="notif-body" />
          </div>

          <button onClick={send} disabled={sending} className="btn-primary flex items-center gap-2" data-testid="notif-send">
            <PaperPlaneTilt size={14} weight="fill" /> {sending ? "Sending…" : "Send Broadcast"}
          </button>

          {/* Preview */}
          {title && body && (
            <div className="mt-6">
              <div className="qd-overline mb-2">Preview</div>
              <div className="qd-card p-4 max-w-md">
                <div className="font-display text-sm text-white mb-1">{title}</div>
                <div className="text-xs text-white/60">{body}</div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#0E0E0E] p-5">
          <div className="qd-overline mb-3">Smart Presets</div>
          <div className="space-y-2">
            {PRESETS.map(p => (
              <button key={p.id} onClick={() => applyPreset(p)} className="w-full text-left p-3 border border-[#2A2A2A] hover:border-[#FF1E1E]/50 bg-[#0F0F0F]" data-testid={`preset-${p.id}`}>
                <div className="font-display text-xs text-white">{p.title}</div>
                <div className="text-[11px] text-white/55 mt-1 line-clamp-2">{p.body}</div>
              </button>
            ))}
          </div>

          <div className="qd-overline mt-6 mb-3">Auto Triggers</div>
          <div className="space-y-2 text-xs text-white/60">
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-500"/>New match found (≥60%) → buyer</div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-500"/>Inspection scheduled → seller</div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-500"/>Live offer added → seller</div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/30"/>Stale buyer (≥7d inactive) → re-engagement [manual]</div>
          </div>
        </div>
      </div>
    </div>
  );
}
